import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { MagnifyingGlass } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_TONES = {
  "PO":                 "bg-teal-500/15 text-teal-300 border-teal-700/40",
  "Order":              "bg-sky-500/15 text-sky-300 border-sky-700/40",
  "BOL":                "bg-violet-500/15 text-violet-300 border-violet-700/40",
  "AWB":                "bg-blue-500/15 text-blue-300 border-blue-700/40",
  "Container":          "bg-orange-500/15 text-orange-300 border-orange-700/40",
  "Load":               "bg-amber-500/15 text-amber-300 border-amber-700/40",
  "Pickup":             "bg-lime-500/15 text-lime-300 border-lime-700/40",
  "Manifest":           "bg-pink-500/15 text-pink-300 border-pink-700/40",
  "Delivery Order":     "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-700/40",
  "Master Tracking":    "bg-cyan-500/15 text-cyan-300 border-cyan-700/40",
  "Shipment":           "bg-zinc-700 text-zinc-300 border-zinc-600",
  "Work Order":         "bg-emerald-500/15 text-emerald-300 border-emerald-700/40",
  "Customer Reference": "bg-rose-500/15 text-rose-300 border-rose-700/40",
  "Custom":             "bg-zinc-700 text-zinc-300 border-zinc-600",
};

/**
 * Lazy-loaded reference autocomplete.
 * - Fetches /api/shipments/recent-references ONLY on first focus (not on mount).
 * - Suggestions are org-scoped server-side; cross-tenant leakage is impossible.
 * - Selecting a suggestion sets the search box value, which the parent
 *   already wires into the existing /shipments/?q= query.
 */
export default function ReferenceAutocomplete({ value, onChange }) {
  const [suggestions, setSuggestions] = useState(null);   // null until first focus
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  const loadIfNeeded = async () => {
    if (suggestions !== null) return;       // already loaded once this session
    setLoading(true);
    try {
      const res = await axios.get(`${API}/shipments/recent-references`, { params: { limit: 20 } });
      setSuggestions(res.data?.items || []);
    } catch {
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Filter the cached suggestions client-side as the user types.
  const filtered = (suggestions || []).filter(s => {
    if (!value) return true;
    const v = value.toLowerCase();
    return (s.value || "").toLowerCase().includes(v) || (s.type || "").toLowerCase().includes(v);
  });

  return (
    <div ref={ref} className="relative" data-testid="ref-autocomplete">
      <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => { loadIfNeeded(); setOpen(true); }}
        placeholder="Search by name, PO, BOL, AWB, container, work order…"
        className="pl-9 h-9 bg-zinc-900 border-zinc-800 text-sm"
        data-testid="shipment-search"
      />
      {open && suggestions !== null && (suggestions.length > 0 || loading) && (
        <div
          className="absolute left-0 right-0 mt-1 bg-zinc-950 border border-zinc-800 rounded shadow-xl z-50 max-h-80 overflow-y-auto"
          data-testid="ref-autocomplete-menu"
        >
          <div className="px-3 py-1.5 border-b border-zinc-800 text-[9px] uppercase tracking-wider text-zinc-500 flex items-center justify-between">
            <span>Recent references{value ? " · matching" : ""}</span>
            {loading && <span className="text-zinc-600 italic">loading…</span>}
          </div>
          {filtered.length === 0 ? (
            <div className="px-3 py-3 text-[11px] text-zinc-500 italic">
              {value ? `No reference matches “${value}”.` : "No references yet."}
            </div>
          ) : (
            filtered.slice(0, 20).map((s, i) => (
              <button
                key={`${s.type}::${s.value}::${i}`}
                onClick={() => { onChange(s.value); setOpen(false); }}
                className="w-full text-left px-3 py-1.5 hover:bg-zinc-900 flex items-center gap-2 text-[12px] border-b border-zinc-900 last:border-b-0"
                data-testid={`ref-suggestion-${i}`}
              >
                <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded border ${TYPE_TONES[s.type] || TYPE_TONES.Custom}`}>
                  {(s.label || s.type || "REF").slice(0, 18)}
                </span>
                <span className="font-mono text-zinc-200 truncate">{s.value}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
